// filepath: apps/web/src/design-system/Button.tsx
//
// FR-UI-CORE-9 / FR-UI-OPS-3 — Button primitive from the design system
// inventory (`docs/guides/design system/components/core/Button.jsx`).
//
// Ported from the reference JSX to TypeScript for the production
// React SPA. All colors reference semantic design-system aliases via
// CSS custom properties; no raw hex values are introduced here.

import React from "react";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md";

export interface ButtonProps {
  children?: React.ReactNode;
  /** Visual style. Default: `primary`. */
  variant?: ButtonVariant;
  /** Vertical sizing scale. Default: `md`. */
  size?: ButtonSize;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Native button type — `submit` for forms. Default: `button`. */
  type?: "button" | "submit" | "reset";
  /** Test hook forwarded onto the rendered element. */
  "data-testid"?: string;
  /** Accessible label override (for icon-only buttons). */
  "aria-label"?: string;
  /** Extra className passthrough. */
  className?: string;
  /** Inline style passthrough (token-only). */
  style?: React.CSSProperties;
}

interface VariantPalette {
  bg: string;
  hoverBg: string;
  text: string;
  border: string;
}

const VARIANT_PALETTES: Record<ButtonVariant, VariantPalette> = {
  primary: {
    bg: "var(--color-primary)",
    hoverBg: "var(--color-primary-hover)",
    text: "var(--text-on-primary)",
    border: "var(--color-primary)",
  },
  secondary: {
    bg: "var(--surface-card)",
    hoverBg: "var(--surface-hover)",
    text: "var(--text-body)",
    border: "var(--border-strong)",
  },
  danger: {
    bg: "var(--color-danger)",
    hoverBg: "var(--color-danger-hover)",
    text: "var(--text-on-primary)",
    border: "var(--color-danger)",
  },
  ghost: {
    bg: "transparent",
    hoverBg: "var(--surface-hover)",
    text: "var(--text-body)",
    border: "transparent",
  },
};

interface SizeSpec {
  padding: string;
  font: string;
}

const SIZE_SPECS: Record<ButtonSize, SizeSpec> = {
  sm: { padding: "4px 12px", font: "var(--text-sm)" },
  md: { padding: "8px 16px", font: "var(--text-base)" },
};

/**
 * Button — semantic button primitive. Pure presentation; no internal
 * state. Hover background uses CSS `:hover` so the color flips via
 * CSS instead of a React re-render (preserves the reference
 * implementation's hover behavior using a state hook).
 */
export function Button({
  children,
  variant = "primary",
  size = "md",
  disabled = false,
  onClick,
  type = "button",
  style,
  className,
  ...rest
}: ButtonProps): React.ReactElement {
  const palette = VARIANT_PALETTES[variant] ?? VARIANT_PALETTES.primary;
  const sizeSpec = SIZE_SPECS[size] ?? SIZE_SPECS.md;
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={[
        "ds-button",
        `ds-button--${variant}`,
        `ds-button--${size}`,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        padding: sizeSpec.padding,
        fontSize: sizeSpec.font,
        fontFamily: "var(--font-sans)",
        fontWeight: "var(--font-medium)",
        background: disabled ? "var(--color-primary-disabled)" : palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        borderRadius: "var(--radius-sm)",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background var(--duration-fast) var(--ease-standard)",
        ...style,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = palette.hoverBg;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = palette.bg;
      }}
    >
      {children}
    </button>
  );
}
